import type { LucideIcon } from "lucide-react";
import type { InputHTMLAttributes, ReactNode, Ref, TextareaHTMLAttributes } from "react";
import { cn } from "@/shared/lib/utils";
import { controlBase, controlSizes, type ControlSize } from "./control-styles";
import { useFieldControlProps } from "./field-context";

type InputProps = Omit<InputHTMLAttributes<HTMLInputElement>, "size"> & {
  size?: ControlSize;
  invalid?: boolean;
  /** Ícone à esquerda (busca, usuário…). */
  icon?: LucideIcon;
  /** Conteúdo à direita (botão de mostrar senha, unidade…). */
  trailing?: ReactNode;
  ref?: Ref<HTMLInputElement>;
};

/**
 * Campo de texto. Dentro de <FormField> recebe id/aria automaticamente.
 * Retrocompatível: `className` continua indo para o <input>.
 */
export function Input({ className, size = "md", invalid, icon: Icon, trailing, ...props }: InputProps) {
  const field = useFieldControlProps(props, invalid);
  const input = (
    <input
      {...props}
      id={field.id}
      aria-describedby={field["aria-describedby"]}
      aria-invalid={field["aria-invalid"]}
      required={field.required}
      disabled={field.disabled}
      className={cn(controlBase, controlSizes[size], "w-full", Icon && "pl-9", trailing != null && "pr-10", className)}
    />
  );
  if (!Icon && trailing == null) return input;
  return (
    <div className="relative w-full min-w-0">
      {Icon ? <Icon className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-fg-subtle" aria-hidden /> : null}
      {input}
      {trailing != null ? <div className="absolute inset-y-0 right-1 flex items-center">{trailing}</div> : null}
    </div>
  );
}

type TextareaProps = TextareaHTMLAttributes<HTMLTextAreaElement> & { invalid?: boolean; ref?: Ref<HTMLTextAreaElement> };

export function Textarea({ className, invalid, rows = 4, ...props }: TextareaProps) {
  const field = useFieldControlProps(props, invalid);
  return (
    <textarea
      rows={rows}
      {...props}
      id={field.id}
      aria-describedby={field["aria-describedby"]}
      aria-invalid={field["aria-invalid"]}
      required={field.required}
      disabled={field.disabled}
      className={cn(controlBase, "w-full px-3 py-2 leading-relaxed", className)}
    />
  );
}
